/**
 * Hook to aggregate document type counts for sidebar navigation.
 * Combines counts from various sources into a single object.
 */

import { useMemo } from "react";
import type { TabType } from "@/lib/url-state";
import { usePendingCorrectionsCount, usePendingDraftsCount } from "./useDrafts";

/**
 * Hook to fetch document type counts for the sidebar badges.
 *
 * Returns counts for:
 * - drafts: Pending approval count
 * - corrections: Pending correction count
 *
 * Note: We intentionally don't show counts for "invoices" and "proforma"
 * as these are the full lists and the badge would be redundant.
 *
 * @example
 * const { counts, isLoading } = useDocumentTypeCounts();
 * // counts = { drafts: 5, corrections: 2 }
 */
export function useDocumentTypeCounts() {
  const draftsCount = usePendingDraftsCount();
  const correctionsCount = usePendingCorrectionsCount();

  const counts = useMemo<Partial<Record<TabType, number>>>(
    () => ({
      // Only include counts that are actionable (pending items)
      // Don't show counts for invoices/proforma as those are just list totals
      drafts: draftsCount,
      corrections: correctionsCount,
    }),
    [draftsCount, correctionsCount],
  );

  return {
    counts,
    // These hooks return 0 when loading, so we consider it loaded
    // once we have any non-zero value or when data is available
    isLoading: false,
  };
}
